import { useContext } from 'react';
import {
  CurrencyCode,
  GiftCardItem,
  OrderCreateRequest,
  PurchaseItem,
  PurchaseItemConfiguration,
  PurchaseItemType,
  ShippingAddress,
  SubscriptionPlanCode,
  OrderCreateResponse,
  SubscriptionService,
  PaymentType
} from '@dayetopia/types';
import { useLocation } from '@reach/router';
import * as Sentry from '@sentry/react';
import { signOut } from 'firebase/auth';
import { Context } from '@components/Context';
import { isMultiMonth } from '@components/dashboard/utils/subscriptions';
import { PAD_STACK_PRODUCT_IDS } from '@constants';
import { CartItem, CartItemType, PadSizeType, ProductCode, SubscriptionCartItem } from '@contracts';
import Checkout from '@utils/checkout';
import { addBillingInfoTracking, addPaymentInfoTracking, checkoutTrack } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';
import { isPadsAndStackBox } from './pads';
import { buildPurchaseItemConfiguration, calculatePricePerQuantity, getBaseVariantOnDemand } from './product';
import { setOnSessionStorage } from './sessionStorage';

// Helper to check if we're in a test environment
const isTestEnvironment = process.env.NODE_ENV === 'test';

interface Props {
  setLoading: (a: boolean) => void;
  setErrorMessage: (a: string) => void;
  setErrorList: (a: string[]) => void;
  handle3DS: (a: string) => void;
}

export interface GiftCardCartItem extends CartItem {
  details: GiftCardItem;
}

export type OnApproveData = {
  billingToken?: string | null;
  facilitatorAccessToken: string;
  orderID: string;
  payerID?: string | null;
  paymentID?: string | null;
  subscriptionID?: string | null;
  authCode?: string | null;
};

export type CheckoutParams = {
  tokenId: string;
  coupon: string;
  subscriptionServiceUsed: SubscriptionService;
  paymentType: PaymentType;
  shippingAddress: ShippingAddress;
  threeDSecureActionResultTokenId?: string | null;
  isPayPalOrderPreview?: boolean;
  payPalOrderData?: OnApproveData;
  primaryPaymentMethod?: boolean;
  expressCartItems?: CartItem[];
  billingInfoId?: string;
  previewOrderProcessor?: (order: any) => void;
  email?: string;
};

export class GiftCardDetailsRequest {
  constructor(recipient: { name: string; email: string }, sender: { name: string }, message: string, amount: number) {
    this.recipient = recipient;
    this.sender = sender;
    this.message = message;
    this.amount = amount;
  }

  recipient: {
    name: string;
    email: string;
  };

  sender: {
    name: string;
  };

  message: string;

  amount: number;
}

export default function useCheckout({ setLoading, setErrorMessage, setErrorList, handle3DS }: Props) {
  const { state } = useContext(Context);
  const { navigateWrapper } = useNavigate();
  const { currency, cartItems, shippingCost, country, discountAmount, firebaseAuth } = state;
  let checkout: Checkout;
  let userId: string;
  let userEmail: string;
  const location = useLocation();

  const handleCheckout = async (params: CheckoutParams) => {
    if (!firebaseAuth) return;
    const orderItems = params.expressCartItems ?? cartItems;
    setLoading(true);
    setErrorMessage('');
    setErrorList([]);

    function handleError(error: any) {
      Sentry.captureException(error, { tags: { error_id: 'CheckoutError' } });
      Sentry.captureMessage('Checkout failed');
      if (typeof error === 'string') {
        setErrorMessage(error);
      } else if (error.error) {
        setErrorMessage(error.error);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else if (error.details) {
        const errorStrings: string[] = [];
        error.details.forEach((err: { field: string; messages: string[] }) => {
          errorStrings.push(`card ${err.field} ${err.messages ? err.messages[0] : ''}`);
        });
        setErrorMessage(`Something went wrong with your payment: ${errorStrings.join(', ')}. Please check your details and try again.`);
      } else {
        setErrorMessage(`Something went wrong with your payment. Please contact customer support.`);
      }
    }

    function getCurrencyCode() {
      return currency.letterCode;
    }

    const createOrderHandling = async (): Promise<OrderCreateResponse> => {
      const body: any = createOrderBody();

      if (params.previewOrderProcessor) {
        body.billingInfo = {
          address: params.shippingAddress,
          primaryPaymentMethod: params.primaryPaymentMethod
        };
      } else {
        body.billingInfo = {
          tokenId: params.tokenId,
          primaryPaymentMethod: params.primaryPaymentMethod
        };
        if (params.threeDSecureActionResultTokenId) {
          body.billingInfo.threeDSecureActionResultTokenId = params.threeDSecureActionResultTokenId;
        }
      }
      if (params.isPayPalOrderPreview) {
        body.isPayPalOrderPreview = true;
      }
      if (params.payPalOrderData) {
        body.payPalOrderData = params.payPalOrderData;
      }

      if (params.subscriptionServiceUsed === SubscriptionService.Flex) {
        // Price set to 0 because backend expects it
        // but we currently we do not calculate it for Flex
        body.tax = 0;
        body.redirectUrl = `${location.origin}/en-us`;
      }

      const result: OrderCreateResponse = await checkout.createOrder(body, params.previewOrderProcessor);

      if (params.previewOrderProcessor) {
        params.previewOrderProcessor(result);
        return result;
      }

      if (params.isPayPalOrderPreview) {
        return result;
      }

      if (result.requires3DS) {
        Sentry.captureMessage('3DS token required on checkout');
        handle3DS(result.actionToken3DS ?? '');
        return result;
      }

      if (result.invoice) {
        const { invoice, createdOrderId, purchasedItems, couponCodesUsed } = result;
        try {
          setOnSessionStorage('feedbackStep', 1);
          const couponsString = couponCodesUsed ? couponCodesUsed.join(',') : '';
          const { paymentMethod } = invoice;
          const cardType = paymentMethod?.cardType ? paymentMethod?.cardType : '';
          addPaymentInfoTracking(purchasedItems ?? [], cardType, userEmail, params.shippingAddress);
          await checkoutTrack(orderItems, userEmail, invoice, couponsString, cardType, params.shippingAddress);
        } catch (err) {
          // Only log to console in non-test environments
          if (!isTestEnvironment) {
            console.debug('Error: ', JSON.stringify(err));
          }
          Sentry.captureMessage('Failed to track checkout completion');
        }
        handleOrderSuccess(createdOrderId ?? '');
      } else {
        setLoading(false);
      }

      return result;
    };

    function handleOrderSuccess(orderId: string) {
      if (params?.subscriptionServiceUsed !== SubscriptionService.Flex) {
        navigateWrapper(`/checkout/order-success?id=${orderId}`);
      }
    }

    function createOrderBody() {
      const orderData: OrderCreateRequest = {
        email: params.email ?? userEmail,
        paymentType: params.paymentType,
        currency: getCurrencyCode() as CurrencyCode,
        accountId: userId,
        orderShippingCost: getOrderShippingCost(),
        purchaseItems: buildPurchaseItems(),
        giftCards: buildGiftCardItems(),
        shippingAddressId: params.shippingAddress.id ?? '',
        subscriptionServiceUsed: params.subscriptionServiceUsed,
        country,
        discountAmount: discountAmount ?? 0
      };

      if (params.shippingAddress && orderData.shippingAddressId === '') {
        orderData.shippingAddress = params.shippingAddress ?? '';
      }

      if (!params.tokenId) {
        orderData.billingInfoId = params.billingInfoId;
      }

      if (discountAmount) orderData.couponCodes = [params.coupon?.toLowerCase()];

      return orderData;
    }

    function getOrderShippingCost() {
      return shippingCost.shippingPrice;
    }

    function buildPurchaseItems() {
      const purchaseItems: PurchaseItem[] = [];
      try {
        orderItems.forEach((cartItem: CartItem | SubscriptionCartItem) => {
          const cartItemType = cartItem.type;
          let purchaseItem: PurchaseItem | undefined;
          const splitPurchaseItems: PurchaseItem[] = [];

          switch (cartItemType) {
            case CartItemType.Subscription:
              purchaseItem = buildSubscriptionPurchaseItem(cartItem as SubscriptionCartItem);
              break;
            case CartItemType.OneOffPurchase:
              if (cartItem.productCode === ProductCode.TamponBox || cartItem.productCode === ProductCode.Pads) {
                const { quantity } = cartItem;
                for (let index = 0; index < quantity; index += 1) {
                  const currentPurchaseItem = buildOneOffPurchaseItem(cartItem as CartItem);
                  if (currentPurchaseItem) {
                    currentPurchaseItem.count = 1;
                    splitPurchaseItems.push(currentPurchaseItem);
                  }
                }
              } else {
                purchaseItem = buildOneOffPurchaseItem(cartItem);
              }
              break;
            case CartItemType.Consultation:
            case CartItemType.GiftCard:
              purchaseItem = buildOneOffPurchaseItem(cartItem);
              break;
            default:
              break;
          }

          if (splitPurchaseItems.length > 0) {
            purchaseItems.push(...splitPurchaseItems);
          } else if (purchaseItem) {
            purchaseItems.push(purchaseItem);
          }
        });
      } catch (err) {
        console.error(err);
      }

      return purchaseItems;
    }

    function getSubscriptionItemType(cartItem: SubscriptionCartItem) {
      const { planCode } = cartItem.selectedPlan;
      if (planCode === SubscriptionPlanCode.ScreeningKit) {
        return PurchaseItemType.SubscriptionWithCredits;
      }
      return isMultiMonth(planCode) ? PurchaseItemType.MultiMonthSubscription : PurchaseItemType.SingleMonthSubscription;
    }

    function buildSubscriptionPurchaseItem(cartItem: SubscriptionCartItem) {
      const hasClaimed100PercentCoupon = cartItem.price + (cartItem.shippingFee ?? 0) === 0;
      const shippingFee = hasClaimed100PercentCoupon ? 0 : cartItem.shippingFee ?? 0;
      const pricePerQuantity = calculatePricePerQuantity(cartItem);
      const configuration = buildPurchaseItemConfiguration(cartItem.boxItems ?? [], true, cartItem.productCode, country);

      return {
        variant: getBaseVariantOnDemand(cartItem.productCode),
        count: cartItem.quantity,
        type: getSubscriptionItemType(cartItem),
        planCode: cartItem.selectedPlan.planCode,
        shippingCost: shippingFee,
        actualPrice: pricePerQuantity,
        configuration
      };
    }

    function buildOneOffPurchaseItem(cartItem: CartItem) {
      if (cartItem.id === 'tin_case_free' || cartItem.id === 'tampon_pouch_free') return undefined;

      let variant = cartItem.productCode;

      if (cartItem.productCode === ProductCode.VMSMedicationFee && cartItem.id !== ProductCode.VMSMedicationFee) {
        variant = cartItem.id as ProductCode;
      }

      const purchaseItem: PurchaseItem = {
        variant,
        actualPrice: Number(Number(cartItem.price).toFixed(2)),
        count: cartItem.quantity,
        type: cartItem.type === CartItemType.Consultation ? PurchaseItemType.Consultation : PurchaseItemType.OneOffPurchase
      };

      if (cartItem?.boxItems?.length) {
        const configuration: PurchaseItemConfiguration[] = buildPurchaseItemConfiguration(cartItem.boxItems, false, cartItem.productCode, country);
        purchaseItem.configuration = configuration;
      }

      if (isPadsAndStackBox(cartItem.productCode, country)) {
        purchaseItem.variant = PAD_STACK_PRODUCT_IDS[purchaseItem.variant as PadSizeType];
      }

      return purchaseItem;
    }

    function buildGiftCardItems() {
      const giftCards: GiftCardCartItem[] = orderItems.filter(
        (cartItem: CartItem): cartItem is GiftCardCartItem => cartItem.type === CartItemType.GiftCard
      );

      return giftCards.map(
        (giftCartItem: GiftCardCartItem) =>
          new GiftCardDetailsRequest(
            giftCartItem.details.recipient,
            giftCartItem.details.sender,
            giftCartItem.details.message ?? '',
            giftCartItem.price
          )
      );
    }

    try {
      const { currentUser } = firebaseAuth;
      if (!currentUser) throw Error('Unable to checkout: not currently logged in.');
      const idToken = await currentUser?.getIdToken();
      userId = currentUser?.uid ?? '';
      userEmail = currentUser?.email ?? '';

      if (userEmail) await addBillingInfoTracking(userEmail, orderItems);

      checkout = new Checkout(idToken, (error) => handleError(error));

      return await createOrderHandling();
    } catch (err) {
      handleError(err);
      setLoading(false);
      return undefined;
    } finally {
      try {
        if (firebaseAuth?.currentUser?.isAnonymous) {
          await signOut(firebaseAuth);
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { error_id: 'AnonymousSignOutFailed' } });
      }
    }
  };

  return { handleCheckout };
}
